import { useState, useEffect } from 'react';
import LocationService from '@/service/location.service'; // Import the service
import useDebounce from './useDebounce';
import { useSelector } from "react-redux";

const useLocationAutocomplete = ({ onLocationSelect }) => {
  const currentLocation = useSelector((state) => state.auth.currentLocation);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [previousPlace, setPreviousPlace] = useState(null); // Track previous place ID

  const debouncedQuery = useDebounce(query, 500); // Use the custom hook

  useEffect(() => {
    const fetchLocations = async () => {
      if (debouncedQuery.length > 2) {
        setLoading(true);
        try {
          const response = await LocationService.getAddress(debouncedQuery);
          setSuggestions(response.address || []);
        } catch (err) {
          setError('Failed to fetch locations');
        }
        setLoading(false);
      } else {
        setSuggestions([]);
      }
    };

    fetchLocations();
  }, [debouncedQuery]);

  useEffect(() => {
    const fetchPlaceDetails = async () => {
      if (selectedPlace) {
        setPlaceLoading(true);
        try {
          const details = await LocationService.getGeocode(selectedPlace);
          setPlaceDetails(details);
        } catch (err) {
          setError('Failed to fetch place details');
        }
        setPlaceLoading(false);
      }
    };

    fetchPlaceDetails();
  }, [selectedPlace]);

  useEffect(() => {
    if (onLocationSelect && placeDetails && selectedPlace && selectedPlace !==previousPlace) {
      const locationData = {
        postcode: placeDetails.postcode,
        latitude: placeDetails.latitude,
        longitude: placeDetails.longitude,
        address: placeDetails.formatted_address,
        city: placeDetails.city,
        country: placeDetails.country,
      };
      localStorage.setItem("selectedVendorLocation", JSON.stringify(locationData)); 

      onLocationSelect(locationData);
      setPreviousPlace(selectedPlace); // Update previous place ID
    }
  }, [placeDetails, selectedPlace, onLocationSelect,previousPlace]);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    setPlaceDetails(null);
    setError('');
  };

  const handleSelectPlace = (place) => {
    setQuery(place);
    setSelectedPlace(place);
    setSuggestions([]);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setPlaceDetails(null);
    setSelectedPlace(null);
    setPreviousPlace(null); // Reset previous place ID
    const { postcode = "", city = "", latitude = "", longitude = "" } = currentLocation;
    const locationData = {
      postcode: postcode,
      city: city,
      latitude: latitude,
      longitude: longitude,
    };
    localStorage.setItem("selectedVendorLocation", JSON.stringify(locationData)); 
    if (onLocationSelect) {
      onLocationSelect({ postcode, city, latitude, longitude });
    }
  };

  return {
    query,
    suggestions,
    loading,
    error,
    placeDetails,
    placeLoading,
    setSuggestions,
    handleInputChange,
    handleSelectPlace,
    handleClear,
    setQuery,
  };
};

export default useLocationAutocomplete;
